import Emitter from "es6-event-emitter";
import APIManager from "./../../common/utilities/apimanager";
import SubScriptionManagerFree from "./../../common/subscriptionmanagement/subscriptionmanagerfree";
import SubScriptionManagerSubscription from "./../../common/subscriptionmanagement/subscriptionmanagersubscription";
import {
  getPaymentMethod,
  checkAuthorizationForPublication,
} from "./downloadAuthorization";
import paymentMethods from "./paymentMethods";
import {
  isAlreadyAuthorizedForContentPackage,
  isUserNotLoggedInButHasContentAccess,
  setUserNotLoggedInButHasContentAccess,
} from "../services/readerSession";
import { AUTHORIZED } from "../analytics/analyticsevents";

/**
 * Contstructs the AuthenticatorAuthorisationController
 */
export default class AuthenticatorAuthorisationController extends Emitter {
  _appState = null;

  _sessionManager = null;
  _cookieManager = null;
  _subscriptionManager = null;

  _options = null;

  constructor(appState, sessionManager, cookieManager, options = {}) {
    super();
    this._appState = appState;
    this._sessionManager = sessionManager;
    this._cookieManager = cookieManager;
    this._options = options;
    this._initializeSubScriptionManager(options);
    this._initListeners();
  }

  /**
   * Returns the current authentication state
   * @return {Boolean}
   */
  get isAuthenticated() {
    return this._subscriptionManager.isAuthenticated;
  }

  logOut() {
    return this._subscriptionManager.logOut();
  }

  authenticate() {
    const { downloadToken } = this._appState;
    return this._subscriptionManager.authenticate(downloadToken);
  }

  authenticateByUserNamePassword(username, password) {
    const { downloadToken } = this._appState;
    return this._subscriptionManager.authenticateByUserNamePassword(
      username,
      password,
      downloadToken
    );
  }

  async _checkIsPublicationAccessAuthorized(
    publicationData,
    withConfirmDownload
  ) {
    const authorizeFlow = async () => {
      const authorizeDownloadParams = await this._getAuthorizationParams(
        publicationData
      );
      await checkAuthorizationForPublication(
        authorizeDownloadParams,
        withConfirmDownload
      );
      if (authorizeDownloadParams.paymentMethod === paymentMethods.UNKNOWN) {
        setUserNotLoggedInButHasContentAccess(true);
        return;
      }
    };

    try {
      await authorizeFlow();
    } catch (error) {
      if (error && error.code && error.code === "E29") {
        const { downloadToken } = this._appState;
        await this._subscriptionManager.authenticate(downloadToken);
        // Either the above succeeds, meaning the user is properly logged in (but was not before), so we recheck the authorization
        // Or the above fails by throwing an error, meaning the user is not logged in. The throw will be caught more upstream, showing a popup or paywall
        await authorizeFlow();
        return;
      }
      throw error;
    }
  }

  async _getAuthorizationParams(publicationData) {
    const subscriptionId = this._getSubscriptionId();
    const paymentMethod = getPaymentMethod(subscriptionId, paymentMethods);

    const sessionId = await this._sessionManager.getSessionInfo();
    const userId = this._sessionManager.userId;
    return {
      sessionId,
      userId,
      subscriptionId,
      contentPackageId: publicationData.contentPackageId,
      paymentMethod,
    };
  }

  async authorize(publicationData, confirmDownload) {
    if (
      isAlreadyAuthorizedForContentPackage(publicationData.contentPackageId)
    ) {
      return;
    }

    await this._checkIsPublicationAccessAuthorized(
      publicationData,
      confirmDownload
    );
    this._appState.triggerEvent(AUTHORIZED);
  }

  authorizePage({ contentPackageId, publicationId, pageId }) {
    if (isUserNotLoggedInButHasContentAccess()) {
      return Promise.resolve();
    }
    return this._subscriptionManager.authorizePage(
      contentPackageId,
      publicationId,
      pageId
    );
  }

  authorizeArticle(contentPackageId, publicationId, pageId, contentItemId) {
    if (isUserNotLoggedInButHasContentAccess()) {
      return Promise.resolve();
    }
    return this._subscriptionManager.authorizeArticle(
      contentPackageId,
      publicationId,
      pageId,
      contentItemId
    );
  }

  authorizeByUserNamePassword(
    username,
    password,
    publicationData,
    confirmDownload
  ) {
    const { contentPackageId, publicationId } = publicationData;

    return this.authenticateByUserNamePassword(username, password)
      .then(() =>
        this.authorize({ contentPackageId, publicationId }, confirmDownload)
      )
      .then(() => {
        return { contentPackageId, publicationId };
      });
  }

  /////////

  _initListeners() {
    this._subscriptionManager.on(
      "subscriptionmanager:authenticationstatuschanged",
      this._onAuthenticationStatusChanged.bind(this)
    );
  }

  /**
   * Depending on options/managers instantiates right _subscriptionManager
   * @param  {[String]} subscriptionType
   */
  _initializeSubScriptionManager({
    subscriptionOptions: { subscriptionType },
  }) {
    switch (subscriptionType) {
      case "free":
        this._subscriptionManager = new SubScriptionManagerFree(
          this._appState,
          this._sessionManager,
          APIManager,
          this._cookieManager,
          this._options
        );
        break;
      case "subscription":
        this._subscriptionManager = new SubScriptionManagerSubscription(
          this._appState,
          this._sessionManager,
          APIManager,
          this._cookieManager,
          this._options
        );
        break;
      case "freemium":
        //TODO
        break;
      case "metered":
        //TODO
        break;
      default:
    }
  }

  _onAuthenticationStatusChanged() {
    this._appState.loggedIn = this._subscriptionManager.isAuthenticated;
  }

  _getSubscriptionId() {
    const cookie = this._cookieManager.getCookie(
      this._cookieManager.cookieKeys.LOGIN
    );
    if (cookie) {
      try {
        return JSON.parse(cookie).subsId;
      } catch (error) {
        return 0;
      }
    }
    return 0;
  }

  getSSOState() {
    return this._subscriptionManager.getSSOState();
  }

  createSSOStateCookie(publicationEncodedURL = null) {
    return this._subscriptionManager.createSSOStateCookie(
      publicationEncodedURL
    );
  }

  removeSSOStateCookie() {
    this._subscriptionManager.removeSSOStateCookie();
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/authenticatorAuthorisationController.js